import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Sparkles, Heart, Send, TrendingUp, BookOpen, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useLanguage } from '@/i18n/LanguageContext';

const STORAGE_KEY = 'treslog_onboarding_dismissed';

interface TreslogOnboardingProps {
  onComplete: () => void;
}

interface Slide {
  icon: React.ReactNode;
  title: string;
  description: string;
  tip?: string;
}

export function TreslogOnboarding({ onComplete }: TreslogOnboardingProps) {
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const content = {
    en: {
      slides: [
        {
          icon: <Sparkles className="h-12 w-12 text-primary" />,
          title: 'Welcome to TRESLOG',
          description: 'Your private space for self-reflection and emotional wellness tracking.',
          tip: 'TRESLOG helps you understand your emotional patterns over time.',
        },
        {
          icon: <Heart className="h-12 w-12 text-red-500" />,
          title: 'Check In With Yourself',
          description: 'Use the mood meter to record how you\'re feeling. Select an emoji that matches your current state.',
          tip: 'You can add optional notes to give context to your mood.',
        },
        {
          icon: <Send className="h-12 w-12 text-blue-500" />,
          title: 'Share or Keep Private',
          description: 'Choose to keep your mood entries private, or send them to someone who cares about you.',
          tip: 'When you share, recipients will know it\'s from Say It NOW.',
        },
        {
          icon: <TrendingUp className="h-12 w-12 text-green-500" />,
          title: 'Track Your Journey',
          description: 'View your mood trends over the week or month. See how your emotional wellness changes over time.',
          tip: 'Toggle between weekly and monthly views for different perspectives.',
        },
        {
          icon: <BookOpen className="h-12 w-12 text-purple-500" />,
          title: 'Write Your Story',
          description: 'Create journal entries to capture memories, thoughts, and reflections in third person.',
          tip: 'Third-person writing helps you gain perspective on your experiences.',
        },
      ] as Slide[],
      neverShow: 'Got it, don\'t show again',
      showLater: 'Show me later',
      next: 'Next',
      previous: 'Back',
      getStarted: 'Get Started',
      stepOf: 'of',
    },
    es: {
      slides: [
        {
          icon: <Sparkles className="h-12 w-12 text-primary" />,
          title: 'Bienvenido a TRESLOG',
          description: 'Tu espacio privado para la reflexión personal y el seguimiento del bienestar emocional.',
          tip: 'TRESLOG te ayuda a entender tus patrones emocionales a lo largo del tiempo.',
        },
        {
          icon: <Heart className="h-12 w-12 text-red-500" />,
          title: 'Registra Cómo Te Sientes',
          description: 'Usa el medidor de ánimo para registrar cómo te sientes. Selecciona un emoji que represente tu estado actual.',
          tip: 'Puedes añadir notas opcionales para dar contexto a tu estado de ánimo.',
        },
        {
          icon: <Send className="h-12 w-12 text-blue-500" />,
          title: 'Comparte o Mantén Privado',
          description: 'Elige mantener tus entradas de ánimo privadas, o envíalas a alguien que se preocupa por ti.',
          tip: 'Cuando compartes, los destinatarios sabrán que es de Say It NOW.',
        },
        {
          icon: <TrendingUp className="h-12 w-12 text-green-500" />,
          title: 'Sigue Tu Viaje',
          description: 'Ve tus tendencias de ánimo durante la semana o el mes. Observa cómo cambia tu bienestar emocional.',
          tip: 'Alterna entre vistas semanales y mensuales para diferentes perspectivas.',
        },
        {
          icon: <BookOpen className="h-12 w-12 text-purple-500" />,
          title: 'Escribe Tu Historia',
          description: 'Crea entradas de diario para capturar recuerdos, pensamientos y reflexiones en tercera persona.',
          tip: 'Escribir en tercera persona te ayuda a ganar perspectiva sobre tus experiencias.',
        },
      ] as Slide[],
      neverShow: 'Entendido, no mostrar de nuevo',
      showLater: 'Mostrar después',
      next: 'Siguiente',
      previous: 'Atrás',
      getStarted: 'Comenzar',
      stepOf: 'de',
    },
  };

  const t = content[language as keyof typeof content] || content.en;
  const slides = t.slides;
  const totalSlides = slides.length;

  useEffect(() => {
    // Check if user has dismissed the onboarding
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed !== 'true') {
      // Small delay to let the page load first
      const timer = setTimeout(() => setOpen(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleNeverShow = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setOpen(false);
    onComplete();
  };

  const handleShowLater = () => {
    // Don't save to localStorage, will show again next time
    setOpen(false);
    onComplete();
  };

  const handleNext = () => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentSlide > 0) {
      setCurrentSlide((prev) => prev - 1);
    }
  };

  const handleGetStarted = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setOpen(false);
    onComplete();
  };

  const currentSlideData = slides[currentSlide];
  const isLastSlide = currentSlide === totalSlides - 1;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden">
        {/* Close button */}
        <button
          onClick={handleShowLater}
          className="absolute right-4 top-4 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>

        <div className="p-6 pt-8">
          {/* Progress dots */}
          <div className="flex justify-center gap-2 mb-6">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? 'bg-primary w-6'
                    : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                }`}
              />
            ))}
          </div>

          {/* Slide content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="text-center"
            >
              {/* Icon */}
              <div className="flex justify-center mb-6">
                <div className="p-4 rounded-2xl bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10">
                  {currentSlideData.icon}
                </div>
              </div>

              {/* Title */}
              <h2 className="text-xl font-bold text-foreground mb-3">
                {currentSlideData.title}
              </h2>

              {/* Description */}
              <p className="text-muted-foreground mb-4 leading-relaxed">
                {currentSlideData.description}
              </p>

              {/* Tip */}
              {currentSlideData.tip && (
                <div className="bg-primary/5 border border-primary/10 rounded-lg p-3 text-sm text-primary">
                  💡 {currentSlideData.tip}
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Step counter */}
          <p className="text-center text-xs text-muted-foreground mt-6">
            {currentSlide + 1} {t.stepOf} {totalSlides}
          </p>
        </div>

        {/* Navigation */}
        <div className="border-t border-border bg-muted/30 p-4">
          <div className="flex items-center justify-between gap-3">
            {/* Left side: Previous or Skip */}
            <div className="flex-1">
              {currentSlide > 0 ? (
                <Button variant="ghost" onClick={handlePrevious} className="gap-1">
                  <ChevronLeft className="h-4 w-4" />
                  {t.previous}
                </Button>
              ) : (
                <Button variant="ghost" onClick={handleShowLater} size="sm" className="text-muted-foreground">
                  {t.showLater}
                </Button>
              )}
            </div>

            {/* Right side: Next or Get Started */}
            <div className="flex-1 flex justify-end gap-2">
              {isLastSlide ? (
                <>
                  <Button variant="outline" onClick={handleNeverShow} size="sm">
                    {t.neverShow}
                  </Button>
                  <Button onClick={handleGetStarted} className="gap-1">
                    {t.getStarted}
                    <Sparkles className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <Button onClick={handleNext} className="gap-1">
                  {t.next}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Hook to check if onboarding should be shown
export function useTreslogOnboarding() {
  const [hasSeenOnboarding, setHasSeenOnboarding] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  });

  const markAsCompleted = () => {
    setHasSeenOnboarding(true);
  };

  const resetOnboarding = () => {
    localStorage.removeItem(STORAGE_KEY);
    setHasSeenOnboarding(false);
  };

  return { hasSeenOnboarding, markAsCompleted, resetOnboarding };
}
